import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';

const POKEAPI = 'https://pokeapi.co/api/v2';

function spriteUrl(pokemonId, shiny) {
  const base = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork';
  return shiny ? `${base}/shiny/${pokemonId}.png` : `${base}/${pokemonId}.png`;
}

function smallSprite(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemonId}.png`;
}

function idFromUrl(url) {
  return parseInt(url.replace(/\/$/, '').split('/').pop(), 10);
}

let cachedPokeList = null;

function PokemonGame() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [pokemon, setPokemon] = useState([]);
  const [pokeList, setPokeList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addShiny, setAddShiny] = useState(false);
  const [addSelected, setAddSelected] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');
  const [editMode, setEditMode] = useState(false);

  const [editingPokemon, setEditingPokemon] = useState(null);
  const [editName, setEditName] = useState('');
  const [editShiny, setEditShiny] = useState(false);
  const [editSelected, setEditSelected] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const fetchGame = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games?id=eq.${id}&select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    setGame(data[0] || null);
  };

  const fetchPokemon = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/game_pokemon?game_id=eq.${id}&order=created_at.asc&select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    setPokemon(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchGame();
    fetchPokemon();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cachedPokeList) { setPokeList(cachedPokeList); return; }
    fetch(`${POKEAPI}/pokemon?limit=100000&offset=0`)
      .then(r => r.json())
      .then(data => {
        cachedPokeList = data.results || [];
        setPokeList(cachedPokeList);
      });
  }, []);

  const suggestions = addName.length >= 2
    ? pokeList
        .filter(p => p.name.includes(addName.toLowerCase().trim()))
        .slice(0, 8)
    : [];

  const editSuggestions = editName.length >= 2
    ? pokeList
        .filter(p => p.name.includes(editName.toLowerCase().trim()))
        .slice(0, 8)
    : [];

  const openAdd = () => {
    setAddName('');
    setAddShiny(false);
    setAddSelected(null);
    setAddStatus('');
    setShowAdd(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addSelected) { setAddStatus('Selecteer een Pokémon.'); return; }
    setAddLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/game_pokemon`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({
          game_id: id,
          name: addSelected.name,
          pokemon_id: addSelected.pokemonId,
          shiny: addShiny,
        }),
      });
      if (!res.ok) throw new Error('Failed to add');
      setAddStatus('Added!');
      setTimeout(() => { setShowAdd(false); fetchPokemon(); }, 800);
    } catch (err) {
      setAddStatus(`Something went wrong: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditingPokemon(p);
    setEditName(p.name);
    setEditShiny(p.shiny || false);
    setEditSelected({ name: p.name, pokemonId: p.pokemon_id });
    setEditStatus('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editSelected) { setEditStatus('Selecteer een Pokémon.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/game_pokemon?id=eq.${editingPokemon.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify({
          name: editSelected.name,
          pokemon_id: editSelected.pokemonId,
          shiny: editShiny,
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditStatus('Saved!');
      setTimeout(() => { setEditingPokemon(null); fetchPokemon(); }, 800);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditDelete = async () => {
    if (!window.confirm(`Delete "${editingPokemon.name}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/game_pokemon?id=eq.${editingPokemon.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setEditingPokemon(null);
      fetchPokemon();
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  if (!game) return null;

  return (
    <div className="app-container">
      <div className="header">
        <h1>{game.name}</h1>
        <div className="controls">
          <button onClick={() => navigate('/pokemon')}>←</button>
          <button onClick={openAdd}>Add +</button>
          {user && (
            <button onClick={() => setEditMode((m) => !m)} className={editMode ? 'btn-active' : ''}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="pokemon-team-grid">
        {pokemon.map((p, idx) => (
          <div
            className="todo-card"
            key={p.id}
            style={{ '--index': idx, cursor: editMode ? 'pointer' : 'default' }}
            onClick={editMode ? () => openEdit(p) : undefined}
          >
            <div className="pokemon-card-img-wrap">
              <img
                src={spriteUrl(p.pokemon_id, p.shiny)}
                alt={p.name}
                className="todo-card-poster todo-card-poster--pokemon"
                onError={(e) => { e.target.onerror = null; e.target.src = smallSprite(p.pokemon_id); }}
              />
              {p.shiny && <img src={require('../Assets/shiny-symbol.png')} alt="shiny" className="pokemon-shiny-badge" />}
            </div>
            <div className="todo-card-title" style={{ textTransform: 'capitalize' }}>{p.name}</div>
          </div>
        ))}
      </div>

      {pokemon.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="modal-title">Add Pokémon</h2>
          <form className="modal-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={addName}
                onChange={(e) => { setAddName(e.target.value); setAddSelected(null); }}
                
                autoFocus
                autoComplete="off"
              />
            </div>

            {suggestions.length > 0 && !addSelected && (
              <div className="pokemon-search-results">
                {suggestions.map((s) => {
                  const pid = idFromUrl(s.url);
                  return (
                    <div
                      key={s.name}
                      className="pokemon-search-result"
                      onClick={() => { setAddSelected({ name: s.name, pokemonId: pid }); setAddName(s.name); }}
                    >
                      <img src={smallSprite(pid)} alt={s.name} className="pokemon-search-img" />
                      <div className="pokemon-search-info">
                        <strong style={{ textTransform: 'capitalize' }}>{s.name}</strong>
                        <span>#{String(pid).padStart(3, '0')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {addSelected && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <img
                  src={spriteUrl(addSelected.pokemonId, addShiny)}
                  alt={addSelected.name}
                  style={{ width: 140 }}
                />
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {addSelected.name} #{String(addSelected.pokemonId).padStart(3, '0')}
                </span>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={addShiny}
                  onChange={(e) => setAddShiny(e.target.checked)}
                />
                Shiny
              </label>
            </div>

            {addStatus && <p className="form-status">{addStatus}</p>}
            <div className="edit-modal-actions">
              <button type="submit" className="form-submit" disabled={addLoading || !addSelected}>
                {addLoading ? 'Working...' : 'Add'}
              </button>
              <button type="button" className="form-submit btn-cancel" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingPokemon && (
        <Modal onClose={() => setEditingPokemon(null)}>
          <h2 className="modal-title">Edit Pokémon</h2>
          <form className="modal-form" onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => { setEditName(e.target.value); setEditSelected(null); }}
                autoFocus
                autoComplete="off"
              />
            </div>

            {editSuggestions.length > 0 && !editSelected && (
              <div className="pokemon-search-results">
                {editSuggestions.map((s) => {
                  const pid = idFromUrl(s.url);
                  return (
                    <div
                      key={s.name}
                      className="pokemon-search-result"
                      onClick={() => { setEditSelected({ name: s.name, pokemonId: pid }); setEditName(s.name); }}
                    >
                      <img src={smallSprite(pid)} alt={s.name} className="pokemon-search-img" />
                      <div className="pokemon-search-info">
                        <strong style={{ textTransform: 'capitalize' }}>{s.name}</strong>
                        <span>#{String(pid).padStart(3, '0')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {editSelected && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <img
                  src={spriteUrl(editSelected.pokemonId, editShiny)}
                  alt={editSelected.name}
                  style={{ width: 140 }}
                />
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {editSelected.name} #{String(editSelected.pokemonId).padStart(3, '0')}
                </span>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editShiny}
                  onChange={(e) => setEditShiny(e.target.checked)}
                />
                Shiny
              </label>
            </div>

            {editStatus && <p className="form-status">{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={() => setEditingPokemon(null)}>
                Cancel
              </button>
              <button type="button" className="form-submit btn-delete" onClick={handleEditDelete}>
                Delete
              </button>
              <button type="submit" className="form-submit" disabled={editLoading || !editSelected}>
                {editLoading ? 'Working...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PokemonGame;
